import { collection, doc, writeBatch } from "firebase/firestore";
import { db } from "../services/firebase";
import { Notebook, MindMapNode, NodeLink } from "../types";

const DEMO_NOTEBOOK_URL = "https://studyboard.app/?mode=view&uid=B9vR650VTGZL36LvnXujA7Hka072&id=1g19y5q";
const ONBOARDING_VERSION = 7;

const generateId = () => doc(collection(db, "_")).id;

interface OnboardingNode {
    id: string;
    notebookId: string;
    title: string;
    content: string;
    position: { x: number; y: number };
    parentId: string | null;
    imageBase64: string;
    createdAt: number;
    updatedAt: number;
    isExpanded: boolean;
    nodeType: string;
    analysis: null;
    tags: never[];
}

const buildOnboardingNodes = (notebookId: string, now: number): OnboardingNode[] => {
    // Pre-generate all IDs
    const ids = {
        root:       generateId(),
        input:      generateId(),
        ai:         generateId(),
        memory:     generateId(),
        pdf:        generateId(),
        tools:      generateId(),
        draw:       generateId(),
        math:       generateId(),
        aiDetail:   generateId(),
        aiLine:     generateId(),
        quiz:       generateId(),
        pdfHL:      generateId(),
        crosslink:  generateId(),
        aiConfig:   generateId(),
    };

    const node = (
        id: string,
        title: string,
        content: string,
        x: number, y: number,
        parentId: string | null,
        img: string
    ): OnboardingNode => ({
        id, notebookId, title, content,
        position: { x, y },
        parentId,
        imageBase64: img,
        createdAt: now, updatedAt: now,
        isExpanded: true, nodeType: "note",
        analysis: null, tags: [],
    });

    return [
        // ── ROOT ──────────────────────────────────────────────
        node(ids.root,
            "👋 Chào mừng đến Study Board!",
            `Không gian học tập dạng bản đồ — mỗi ô là một khái niệm, các đường kết nối thể hiện mối liên hệ.\n\nĐây là notebook mẫu để bạn khám phá. Thử bấm vào từng note!\n\n[Xem notebook thật của người dùng →](${DEMO_NOTEBOOK_URL})`,
            0, 0, null, "https://studyboard.app/ss-02.png"),

        // ── LEVEL 1: 5 nhánh chính ────────────────────────────
        node(ids.input,
            "📸 Nhập nội dung",
            "Upload ảnh slide, chụp màn hình bài giảng, hay dán text vào note. AI tự đọc và gắn hashtag phân loại nội dung.",
            440, -480, ids.root, "https://studyboard.app/ss-04.png"),

        node(ids.ai,
            "🤖 AI Phân tích",
            "Viết xong, bấm AI Check. AI chấm điểm hiểu biết từ 0–100, chỉ ra lỗ hổng kiến thức và gợi ý cải thiện cụ thể.",
            440, -240, ids.root, "https://studyboard.app/ss-06.png"),

        node(ids.memory,
            "🧠 Memory Gym",
            "Tính năng cốt lõi. Đóng note lại, giải thích từ trí nhớ, AI chấm điểm recall. Hiệu quả hơn đọc lại 5 lần.",
            440, 0, ids.root, "https://studyboard.app/ss-01.png"),

        node(ids.pdf,
            "📄 Đọc PDF thông minh",
            "Upload PDF lên — app tự tạo blog view với mục lục từ cấu trúc notebook. AI có thể tóm tắt và tạo notes từ nội dung PDF.",
            440, 240, ids.root, "https://studyboard.app/ss-10.png"),

        node(ids.tools,
            "🔧 Tính năng khác",
            "Tìm kiếm toàn bộ notebook, cross-link khái niệm liên quan, tùy chỉnh AI behavior theo phong cách học của bạn.",
            440, 480, ids.root, "https://studyboard.app/ss-03.png"),

        // ── LEVEL 2: sub-features ─────────────────────────────
        node(ids.draw,
            "✍️ Vẽ & Phác thảo",
            "Vẽ tay trực tiếp trong note — diagram, sơ đồ, đồ thị tay. Lưu ảnh vẽ kèm ghi chú text trong cùng một ô.",
            880, -600, ids.input, "https://studyboard.app/ss-14.png"),

        node(ids.math,
            "∑ Công thức Toán",
            "Gõ LaTeX hoặc chọn ký hiệu từ bảng symbol. Công thức render đẹp ngay trong note — không cần phần mềm ngoài.",
            880, -480, ids.input, "https://studyboard.app/ss-08.png"),

        node(ids.aiDetail,
            "📊 Phân tích chi tiết",
            "Xem điểm mạnh, điểm yếu từng phần trong note. AI phân tích cấu trúc lập luận và đề xuất cách viết lại cho chắc hơn.",
            880, -300, ids.ai, "https://studyboard.app/ss-05.png"),

        node(ids.aiLine,
            "📝 Feedback từng dòng",
            "AI comment trực tiếp vào từng câu — câu nào chính xác, câu nào còn mơ hồ, câu nào thiếu dẫn chứng.",
            880, -180, ids.ai, "https://studyboard.app/ss-13.png"),

        node(ids.quiz,
            "📋 Tạo đề thi tự động",
            "Chọn nhiều notes, bấm Generate Quiz. AI tạo bộ câu hỏi trắc nghiệm từ toàn bộ nội dung — dùng để ôn thi hoặc kiểm tra bản thân.",
            880, 60, ids.memory, "https://studyboard.app/ss-12.png"),

        node(ids.pdfHL,
            "🖊️ Highlight & Annotation",
            "Dùng bút highlight, bút vẽ, hoặc text để đánh dấu trực tiếp lên PDF. Annotation được lưu cùng file — mở lại vẫn còn nguyên.",
            880, 300, ids.pdf, "https://studyboard.app/ss-11.png"),

        node(ids.crosslink,
            "🔗 Cross-link khái niệm",
            "Kéo thả để tạo đường kết nối giữa hai note bất kỳ. Xem các note liên quan ngay trong panel bên — như Wikipedia nhưng là kiến thức của bạn.",
            880, 420, ids.tools, "https://studyboard.app/ss-07.png"),

        node(ids.aiConfig,
            "⚙️ Tùy chỉnh AI",
            "Chọn mức độ nghiêm khắc của AI: từ nhẹ nhàng đến khắt khe. Bật Voice Mode để AI đọc to phân tích — học khi không nhìn màn hình.",
            880, 540, ids.tools, "https://studyboard.app/ss-09.png"),
    ];
};

const buildLinks = (nodes: OnboardingNode[], now: number): NodeLink[] => {
    const byTitle = (title: string) => nodes.find(n => n.title.includes(title))!;

    const parentChildLinks: NodeLink[] = [];
    const root = nodes.find(n => n.parentId === null)!;

    // Parent-child links (solid lines)
    nodes.filter(n => n.parentId !== null).forEach(child => {
        parentChildLinks.push({
            id: generateId(),
            fromId: child.parentId!,
            toId: child.id,
            createdAt: now,
            color: "#6366f1",
        });
    });

    // Cross-links (dashed — minh họa tính năng cross-link)
    const crossLinks: NodeLink[] = [
        {
            id: generateId(),
            fromId: byTitle("AI Phân tích").id,
            toId: byTitle("Memory Gym").id,
            createdAt: now,
            color: "#f59e0b", // amber — study loop
        },
        {
            id: generateId(),
            fromId: byTitle("Nhập nội dung").id,
            toId: byTitle("Đọc PDF").id,
            createdAt: now,
            color: "#10b981", // emerald — cả hai đều nhập liệu
        },
        {
            id: generateId(),
            fromId: byTitle("Phân tích chi tiết").id,
            toId: byTitle("Tạo đề thi").id,
            createdAt: now,
            color: "#f59e0b",
        },
        {
            id: generateId(),
            fromId: byTitle("Cross-link").id,
            toId: root.id,
            createdAt: now,
            color: "#10b981",
        },
    ];

    return [...parentChildLinks, ...crossLinks];
};

export const createOnboardingNotebooks = async (userId: string): Promise<void> => {
    try {
        const batch = writeBatch(db);
        const notebookId = generateId();
        const notebookRef = doc(db, "users", userId, "notebooks", notebookId);
        const now = Date.now();

        const nodes = buildOnboardingNodes(notebookId, now);
        const links = buildLinks(nodes, now);

        batch.set(notebookRef, {
            id: notebookId,
            title: "👋 Getting Started",
            createdAt: now, updatedAt: now,
            isPublic: false,
            mapLayoutStyle: "horizontal",
            nodeWidth: "standard",
            links,
            onboardingVersion: ONBOARDING_VERSION,
        });

        nodes.forEach((node) => {
            const nodeRef = doc(db, "users", userId, "notebooks", notebookId, "nodes", node.id);
            batch.set(nodeRef, node);
        });

        await batch.commit();
        console.log("✅ Onboarding notebooks created v" + ONBOARDING_VERSION);
    } catch (error) {
        console.warn("⚠️ Failed to create onboarding notebooks:", error);
    }
};

// Call after initial data load to upgrade existing Getting Started notebooks
export const refreshOnboardingIfNeeded = async (
    userId: string,
    notebooks: Notebook[]
): Promise<Notebook[]> => {
    try {
        const nb = notebooks.find(n => n.title === "👋 Getting Started");
        if (!nb) return notebooks;
        if ((nb as any).onboardingVersion === ONBOARDING_VERSION) return notebooks;

        const now = Date.now();
        const newNodes = buildOnboardingNodes(nb.id, now);
        const newLinks = buildLinks(newNodes, now);
        const batch = writeBatch(db);

        // Delete old nodes
        nb.nodes.forEach(node => {
            batch.delete(doc(db, "users", userId, "notebooks", nb.id, "nodes", node.id));
        });

        // Write new nodes
        newNodes.forEach(node => {
            batch.set(doc(db, "users", userId, "notebooks", nb.id, "nodes", node.id), node);
        });

        // Bump version + write links
        batch.update(doc(db, "users", userId, "notebooks", nb.id), {
            onboardingVersion: ONBOARDING_VERSION,
            mapLayoutStyle: "horizontal",
            links: newLinks,
            updatedAt: now,
        });

        await batch.commit();
        console.log("✅ Onboarding refreshed to v" + ONBOARDING_VERSION);

        return notebooks.map(n =>
            n.id === nb.id
                ? { ...n, onboardingVersion: ONBOARDING_VERSION, mapLayoutStyle: "horizontal", links: newLinks, nodes: newNodes as any }
                : n
        );
    } catch (error) {
        console.warn("⚠️ Failed to refresh onboarding:", error);
        return notebooks;
    }
};
